import type { AddressFormValue } from "helpers/forms/addressTypes";
import type { CompanyStore } from "services/company/companyStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import type { AbnSoleTraderQuestionsStore } from "services/questions/au/soleTrader/abnSoleTraderQuestionsStore";
import { FormError } from "types/errors";

export enum AbnSoleTraderStepEntity {
  // Corresponds to WhyApplyingForAbn question
  ApplicationReason = "ApplicationReason",

  // Corresponds to FirstTimeRunningBusiness question
  FirstTimeBusinessOrSelfEmployed = "FirstTimeBusinessOrSelfEmployed",

  // Corresponds to FirstTimeSelfEmployed question
  FirstTimeSelfEmployedOrContractor = "FirstTimeSelfEmployedOrContractor",

  // Corresponds to CurrentlyHaveOrPreviouslyHadAbn question
  ExistingAbn = "ExistingAbn",

  // Corresponds to BusinessDescription question
  BusinessDescription = "BusinessDescription",

  // Corresponds to BusinessActivities question
  BusinessActivity = "BusinessActivity",

  // Corresponds to BusinessAddress question
  BusinessAddress = "BusinessAddress",

  // Corresponds to PostalAddress question
  PostalAddress = "PostalAddress",

  // Corresponds to AgriculturalOperations question
  AgriculturalOperations = "AgriculturalOperations",

  // Special: For question navigation flow
  QuestionFlow = "QuestionFlow",
}

export interface AbnSoleTraderFormValues {
  abnApplicationReason?: string;
  abnFirstTimeBusinessOrSelfEmployed?: string;
  abnFirstTimeSelfEmployedOrContractor?: string;
  previousAbn?: string;
  natureOfBusiness?: string;
  businessActivities?: Array<{ code: string; description: string }>;
  businessAddress?: AddressFormValue;
  postalAddress?: AddressFormValue;
  useBusinessAddressAsPostal?: boolean;
  isConductAgriculturalPropertyOperations?: boolean;
  answer?: string; // Generic answer field for question flow
}

export class AbnSoleTraderStepStore {
  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _abnSoleTraderQuestionsStore: AbnSoleTraderQuestionsStore,
  ) {}

  /**
   * Check if all ABN details are complete for sole trader
   */
  checkAllAbnDetailsExist(): boolean {
    const company = this._companyStore.company;
    if (!company) {
      return false;
    }

    // Verify all required fields are saved
    return !!(
      company.abnApplicationReason &&
      company.natureOfBusiness &&
      company.businessActivityCode &&
      company.businessActivityDescription
    );
  }

  /**
   * Main entry point - routes to appropriate handler based on entity
   * For question flow, entity will be QuestionFlow
   */
  async updateEntity(
    data: AbnSoleTraderFormValues,
    entity?: AbnSoleTraderStepEntity,
  ): Promise<{ continueFlow: boolean }> {
    // Default to question flow if no entity specified
    const targetEntity = entity || AbnSoleTraderStepEntity.QuestionFlow;

    switch (targetEntity) {
      case AbnSoleTraderStepEntity.ApplicationReason:
        await this._updateApplicationReason(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.FirstTimeBusinessOrSelfEmployed:
        await this._updateFirstTimeBusinessOrSelfEmployed(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.FirstTimeSelfEmployedOrContractor:
        await this._updateFirstTimeSelfEmployedOrContractor(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.ExistingAbn:
        await this._updateExistingAbn(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.BusinessDescription:
        await this._updateBusinessDescription(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.BusinessActivity:
        await this._updateBusinessActivity(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.BusinessAddress:
        await this._updateBusinessAddress(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.PostalAddress:
        await this._updatePostalAddress(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.AgriculturalOperations:
        await this._updateAgriculturalOperations(data);
        return { continueFlow: true };

      case AbnSoleTraderStepEntity.QuestionFlow:
        return await this._handleQuestionFlow(data);

      default:
        throw new FormError(`Unknown ABN Sole Trader entity: ${String(targetEntity)}`);
    }
  }

  // ==================== Private Entity Update Methods ====================

  private async _updateApplicationReason(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.abnApplicationReason) {
      throw new FormError("No application reason provided");
    }
    await this._companyStore.updateAbnApplicationReason(data.abnApplicationReason);
  }

  private async _updateFirstTimeBusinessOrSelfEmployed(data: AbnSoleTraderFormValues): Promise<void> {
    if (data.abnFirstTimeBusinessOrSelfEmployed === undefined) {
      throw new FormError("No first time business value provided");
    }
    const isFirstTime = data.abnFirstTimeBusinessOrSelfEmployed === "YES";
    await this._companyStore.updateAbnFirstTimeBusinessOrSelfEmployed(isFirstTime);
  }

  private async _updateFirstTimeSelfEmployedOrContractor(data: AbnSoleTraderFormValues): Promise<void> {
    if (data.abnFirstTimeSelfEmployedOrContractor === undefined) {
      throw new FormError("No first time self-employed value provided");
    }
    const isFirstTime = data.abnFirstTimeSelfEmployedOrContractor === "YES";
    await this._companyStore.updateAbnFirstTimeSelfEmployedOrContractor(isFirstTime);
  }

  private async _updateExistingAbn(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.previousAbn) {
      throw new FormError("No existing ABN provided");
    }
    await this._companyStore.updateAbnExistingAbn(data.previousAbn);
  }

  private async _updateBusinessDescription(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.natureOfBusiness) {
      throw new FormError("No business description provided");
    }
    await this._companyStore.updateAbnBusinessDescription(data.natureOfBusiness);
  }

  private async _updateBusinessActivity(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.businessActivities || data.businessActivities.length === 0) {
      throw new FormError("No business activities provided");
    }

    const firstActivity = data.businessActivities[0];
    if (!firstActivity?.code || !firstActivity.description) {
      throw new FormError("Invalid business activity data");
    }

    // Get business address from backend store (already saved in BusinessAddress step)
    const businessAddress = this._companyBusinessActivitiesStore.address;

    if (!businessAddress) {
      throw new FormError(
        "Business address is required to save business activity. Please go back and fill in the business address.",
      );
    }

    // Update company with business activity code and description
    // The address is already saved to backend, so updateActivity will link them together
    await this._companyStore.updateMainBusinessActivity(
      firstActivity.code,
      firstActivity.description,
      businessAddress.id,
    );
  }

  private async _updateBusinessAddress(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.businessAddress) {
      throw new FormError("No business address provided");
    }

    // Save business address to backend API immediately
    await this._companyBusinessActivitiesStore.updateAddress(data.businessAddress);

    // Save checkbox state to formData for postal address logic
    const formDataToSave: Record<string, unknown> = {
      useBusinessAddressAsPostal: data.useBusinessAddressAsPostal,
    };

    // If checkbox is checked, copy business address to postal address
    if (data.useBusinessAddressAsPostal) {
      formDataToSave.postalAddress = data.businessAddress;
    }

    this._abnSoleTraderQuestionsStore.setFormData(formDataToSave);
  }

  private async _updatePostalAddress(data: AbnSoleTraderFormValues): Promise<void> {
    if (!data.postalAddress) {
      throw new FormError("No postal address provided");
    }

    // Save postal address to formData
    // Postal address is metadata that will be used for ATO correspondence
    this._abnSoleTraderQuestionsStore.setFormData({
      postalAddress: data.postalAddress,
    });
  }

  private async _updateAgriculturalOperations(data: AbnSoleTraderFormValues): Promise<void> {
    if (data.isConductAgriculturalPropertyOperations === undefined) {
      throw new FormError("No agricultural operations value provided");
    }
    await this._companyStore.updateAgriculturalPropertyOperations(data.isConductAgriculturalPropertyOperations);
  }

  // ==================== Question Flow Handler ====================

  /**
   * Handles question-based navigation flow
   * Saves data immediately for specific questions, accumulates for others
   */
  private async _handleQuestionFlow(data: AbnSoleTraderFormValues): Promise<{ continueFlow: boolean }> {
    // Save data immediately based on what fields are present
    if (data.abnApplicationReason) {
      await this._updateApplicationReason(data);
    }

    if (data.abnFirstTimeBusinessOrSelfEmployed !== undefined) {
      await this._updateFirstTimeBusinessOrSelfEmployed(data);
    }

    if (data.abnFirstTimeSelfEmployedOrContractor !== undefined) {
      await this._updateFirstTimeSelfEmployedOrContractor(data);
    }

    if (data.previousAbn) {
      await this._updateExistingAbn(data);
    }

    if (data.natureOfBusiness) {
      await this._updateBusinessDescription(data);
    }

    if (data.isConductAgriculturalPropertyOperations !== undefined) {
      await this._updateAgriculturalOperations(data);
    }

    // Handle business address with checkbox (saves to backend immediately)
    if (data.businessAddress) {
      await this._updateBusinessAddress(data);
    }

    // Handle postal address
    if (data.postalAddress) {
      await this._updatePostalAddress(data);
    }

    // Handle business activity (address should already be in backend)
    if (data.businessActivities) {
      await this._updateBusinessActivity(data);
    }

    // Process answer for question navigation - reverse order
    const answer = (() => {
      // Later questions that always use 'continue'
      if (data.isConductAgriculturalPropertyOperations !== undefined) {
        return "continue";
      }
      if (data.businessActivities) {
        return "continue";
      }
      if (data.postalAddress) {
        return "continue";
      }
      if (data.businessAddress) {
        return "continue";
      }
      if (data.natureOfBusiness) {
        return "continue";
      }

      // Earlier questions with specific answer values
      if (data.abnFirstTimeSelfEmployedOrContractor) {
        return data.abnFirstTimeSelfEmployedOrContractor;
      }
      if (data.abnFirstTimeBusinessOrSelfEmployed) {
        return data.abnFirstTimeBusinessOrSelfEmployed;
      }
      if (data.abnApplicationReason) {
        return data.abnApplicationReason;
      }

      // Generic answer field
      if (data.answer) {
        return data.answer;
      }

      return "continue";
    })();

    // Capture current question before any state changes to avoid race conditions
    const currentQuestion = this._abnSoleTraderQuestionsStore.activeQuestionKey;

    const { isFlowComplete } = this._abnSoleTraderQuestionsStore.processAnswer(
      answer,
      data as Record<string, unknown>,
      currentQuestion,
    );

    // Return false if flow is not complete (stay on ABN step, show next question)
    // Return true if flow is complete (advance to next step)
    return { continueFlow: isFlowComplete };
  }
}
